/**
 * Shared formatting for the "clarifying questions answered" public comment.
 *
 * Both entry points that let a reporter answer AI-triage clarifying questions —
 * the admin detail view (AdminIssueDetailView) and the in-overlay post-submit
 * flow (PostSubmitPanel) — write the answers back as a public site_issue_comment.
 * The AI re-triage step and the detail view's Q&A history renderer both key off
 * this exact string shape, so it must stay identical across entry points.
 */

/** Prefix used to detect answered-question comments in the discussion feed. */
export const CLARIFYING_ANSWERS_PREFIX = '💬 **Clarifying Questions Answered:**';

/**
 * Build the public comment body recording a reporter's answers to the AI's
 * clarifying questions. `answers` is keyed by the question's index.
 */
export function formatClarifyingAnswersComment(
    questions: string[],
    answers: Record<number, string>,
): string {
    return (
        `${CLARIFYING_ANSWERS_PREFIX}\n\n` +
        questions
            .map((q, idx) => {
                const ans = answers[idx]?.trim();
                return `**Q:** ${q}\n**A:** ${ans && ans.length > 0 ? ans : '(Skipped)'}`;
            })
            .join('\n\n')
    );
}

/**
 * Heading of a question a team member puts to a staff lodger with "Ask the reporter".
 *
 * A shared contract with `v_team_heading` in `fn_update_site_issue_status_on_comment` (which turns the comment into
 * an open question in the lodger's answer form and moves the card to Awaiting Info) and with TEAM_QUESTION_HEADING
 * in supabase/functions/_shared/issue-questions.ts: keep all three byte-identical.
 */
export const TEAM_QUESTION_HEADING = '❓ **Question from the team**';

/** Build the public comment that asks the lodger one question from the team. */
export function formatTeamQuestionComment(question: string): string {
    return `${TEAM_QUESTION_HEADING}\n\n${question.trim()}`;
}
